import PropTypes from 'prop-types'
import { memo, useCallback, useEffect, useMemo, useState } from 'react'
import Divider from 'components/core/Divider/Divider'
import getUniqueIdString from 'lib/util/uniq'
import styles from './Item.module.scss'
import ItemContext from './context/ItemContext'

/**
 * An Accordion `Item`
 *
 * @param {object} props - the component props
 * @returns {React.ReactElement} the element
 */
function Item (props) {
  const { children, controlled, duration, expanded } = props
  const [isExpanded, setIsExpanded] = useState(expanded)
  const ariaId = useMemo(() => getUniqueIdString('accordion-'), [])
  const onToggle = useCallback(() => {
    setIsExpanded(wasExpanded => !wasExpanded)
  }, [])

  /**
   * Effect: When is controlled, allow `expanded` changes to trigger
   * updates
   */
  useEffect(() => {
    if (!controlled) {
      return
    }

    setIsExpanded(expanded)
  }, [expanded, controlled])

  /**
   * Memo: The context to provide to accordion children
   */
  const ctx = useMemo(
    () => ({
      isExpanded,
      onToggle,
      setIsExpanded,
      ariaId,
      duration
    }),
    [isExpanded, onToggle, setIsExpanded, duration]
  )

  return (
    <ItemContext.Provider value={ctx}>
      <div className={styles.item}>{children}</div>
      <Divider className={styles.divider} />
    </ItemContext.Provider>
  )
}

/** @type {object} */
Item.propTypes = {
  children: PropTypes.node,
  expanded: PropTypes.bool,
  controlled: PropTypes.bool,
  duration: PropTypes.number
}

Item.defaultProps = {
  children: null,
  expanded: true,
  controlled: false,
  duration: 0.25
}

export default memo(Item)
